import React, { useState, useEffect, useCallback } from 'react';
import { useVisitorData } from '../../contexts/VisitorContext';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDwellingUnit } from '../../utils/brandingUtils';
import '../../styles/Dashboard.css';
import api, { getImageUrl } from '../../services/api';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

// Utility function to determine if a color is dark (needs light text)
const isColorDark = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    // Calculate relative luminance using WCAG formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // Return true if the color is dark (needs light text)
    return luminance < 0.5;
  }
  // Default to false for invalid colors
  return false;
};

const Visitors = () => {
  const { visitors, villas, refreshVisitors, loading: dataLoading, error: dataError, connectionError } = useVisitorData();
  const { settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  const [localVisitors, setLocalVisitors] = useState([]);
  const [localVillas, setLocalVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [filterVilla, setFilterVilla] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [visitorStats, setVisitorStats] = useState({
    todayCount: 0,
    weekCount: 0,
    monthCount: 0,
    byVilla: []
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // or whatever number you want per page

  // Create a ref to track if we've already attempted a fetch
  const hasAttemptedFetch = React.useRef(false);
  const loadingTimeoutRef = React.useRef(null);

  // Get dwelling unit name (plural)
  const unitSingular = formatDwellingUnit(settings);
  const unitPlural = formatDwellingUnit(settings, null, true);

  // Cleanup function for timers
  const clearAllTimers = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  // Helper function to update visitor stats
  const updateVisitorStats = useCallback((visitorData, villasData) => {
    try {
      // Calculate visitor stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const oneWeekAgo = new Date(today);
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const oneMonthAgo = new Date(today);
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

      const todayCount = visitorData.filter(v => {
        try {
          return v.time_in && new Date(v.time_in) >= today;
        } catch (e) {
          return false;
        }
      }).length;

      const weekCount = visitorData.filter(v => {
        try {
          return v.time_in && new Date(v.time_in) >= oneWeekAgo;
        } catch (e) {
          return false;
        }
      }).length;

      const monthCount = visitorData.filter(v => {
        try {
          return v.time_in && new Date(v.time_in) >= oneMonthAgo;
        } catch (e) {
          return false;
        }
      }).length;

      // Group visitors by unit - check unit_id field
      const visitorsByVilla = villasData.map(villa => {
        const villaVisitors = visitorData.filter(v =>
          v.unit_id === villa.id
        );
        return {
          villa_id: villa.id,
          villa_number: villa.villa_number,
          unit_type: villa.unit_type || 'Unit',
          count: villaVisitors.length,
          active: villaVisitors.filter(v => v.status === 'active').length
        };
      }).sort((a, b) => b.count - a.count);

      setVisitorStats({
        todayCount,
        weekCount,
        monthCount,
        byVilla: visitorsByVilla.slice(0, 5) // Top 5 villas by visitor count
      });
    } catch (e) {
      console.error("Error updating visitor stats:", e);
    }
  }, []);

  // Handler for manual refresh with forced loading state clearance
  const handleManualRefresh = useCallback(async () => {
    // Clear any previous timers
    clearAllTimers();
    setLoading(true);

    // Safety timeout to ensure we don't get stuck loading
    loadingTimeoutRef.current = setTimeout(() => {
      setLoading(false);
      setError('Refresh timed out. Please try again or check your network connection.');
    }, 5000);

    try {
      // Get user and check organization_id is available
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      if (!user.organization_id) {
        setError('Organization ID is not available. Please log out and log back in.');
        setLoading(false);
        clearAllTimers();
        return;
      }

      // Try refreshing using the context first
      const updatedVisitors = await refreshVisitors();

      if (updatedVisitors && updatedVisitors.length > 0) {
        // Update local state with the refreshed data
        setLocalVisitors(updatedVisitors);
        updateVisitorStats(updatedVisitors, villas || []);
        setError(null); // Clear any existing errors
        clearAllTimers();
      } else {
        try {
          // Get user details from session storage
          const organizationId = user.organization_id;

          if (!organizationId) {
            return;
          }

          console.log('Attempting direct visitor API call...');

          // Direct fetch from API with better error handling
          const response = await api.get('/api/visitors/search', {
            params: {
              organization_id: organizationId,
              limit: 1000,
              status: 'all',
              include_organization: true,
              timestamp: Date.now()
            },
            timeout: 10000, // Shorter timeout for quicker feedback
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });

          // Check if we got HTML instead of JSON
          if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
            console.error('API returned HTML instead of JSON - server/proxy issue detected');
            throw new Error('Server configuration issue: API returning HTML instead of JSON');
          }

          // Process response data
          let fetchedVisitors = [];
          if (response.data) {
            if (Array.isArray(response.data)) {
              fetchedVisitors = response.data;
            } else if (response.data.visitors && Array.isArray(response.data.visitors)) {
              fetchedVisitors = response.data.visitors;
            } else if (response.data.data && Array.isArray(response.data.data)) {
              fetchedVisitors = response.data.data;
            }
          }

          console.log(`Direct API call found ${fetchedVisitors.length} visitors`);

          // Also fetch units/villas
          const unitsResponse = await api.get('/api/units');
          const units = unitsResponse.data || [];

          // Update state
          setLocalVisitors(fetchedVisitors);
          setLocalVillas(units);

          // Calculate visitor stats
          updateVisitorStats(fetchedVisitors, units);

          // Clear any error
          setError(null);

          // After fetch, clear the timeout to prevent automatic state change
          clearAllTimers();
        } catch (directFetchError) {
          console.error("Direct fetch error:", directFetchError);

          // Try one more fallback - mock data for testing
          if (directFetchError.message.includes('HTML instead of JSON')) {
            console.warn('Creating mock visitor data for testing purposes');
            const mockVisitors = [
              {
                id: 1,
                visitor_name: 'Test Visitor 1',
                villa_number: 'A-101',
                unit_id: 1,
                purpose: 'Delivery',
                time_in: new Date().toISOString(),
                time_out: null,
                status: 'active',
                phone: '9876543210'
              },
              {
                id: 2,
                visitor_name: 'Test Visitor 2',
                villa_number: 'B-202',
                unit_id: 2,
                purpose: 'Meeting',
                time_in: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
                time_out: new Date().toISOString(),
                status: 'completed',
                phone: '9876543211'
              }
            ];

            setLocalVisitors(mockVisitors);
            updateVisitorStats(mockVisitors, []);
            setError('Using test data - Server API issue detected. Please contact administrator.');
          } else {
            setError('Failed to retrieve visitor data. Please try again.');
          }
        }
      }
    } catch (err) {
      console.error("Refresh error:", err);
      setError('Failed to refresh visitor data. Please try again.');
    } finally {
      // Always ensure loading state is cleared
      clearAllTimers();
      setLoading(false);
    }
  }, [refreshVisitors, villas, updateVisitorStats, clearAllTimers]);

  // Load initial data when component mounts or when visitors/villas change
  useEffect(() => {
    // Clear previous timeout to avoid memory leaks
    clearAllTimers();

    // Skip if context is still loading
    if (dataLoading) {
      return;
    }

    // Set up a safety timer to ensure loading state is cleared after maximum 3 seconds
    loadingTimeoutRef.current = setTimeout(() => {
      setLoading(false);
      if (!localVisitors.length && !error) {
        console.log("");
        // setError("Loading timed out. Please try refreshing the data.");
      }
    }, 3000);

    try {
      // Make a deep copy of the visitors to avoid reference issues
      const visitorsCopy = visitors ? JSON.parse(JSON.stringify(visitors)) : [];

      if (visitorsCopy.length > 0) {
        // We have data from context
        setLocalVisitors(visitorsCopy);
        setLocalVillas(villas || []);
        updateVisitorStats(visitorsCopy, villas || []);
        setLoading(false);
        clearAllTimers();
      } else if (!hasAttemptedFetch.current) {
        // No data and haven't tried fetching yet
        hasAttemptedFetch.current = true;
        handleManualRefresh();
      }
      /* else {
        // We've already tried fetching and still have no data
        setLoading(false);
        if (!error && !dataError) {
          setError("No visitor data available. Please try refreshing.");
        } else {
          setError(dataError || error);
        }
      } */
    } catch (err) {
      console.error("Error in visitor data initialization:", err);
      setError('Failed to load visitor data. Please try again.');
      setLoading(false);
    }

    // Cleanup on unmount
    return clearAllTimers;
  }, [visitors, villas, dataError, refreshVisitors, dataLoading, error, localVisitors.length, handleManualRefresh, clearAllTimers, updateVisitorStats]);

  // Reset the fetch attempt ref when visitors change
  useEffect(() => {
    if (visitors && visitors.length > 0) {
      // If we have visitors, reset the fetch attempt flag for next time
      hasAttemptedFetch.current = false;
      // Also clear any errors since we have data
      setError(null);
    }
  }, [visitors]);

  // Filter visitors based on active tab, search term, filter date, and filter villa
  const filteredVisitors = localVisitors.filter(visitor => {
    // Filter by tab
    if (activeTab === 'active' && visitor.status !== 'active') return false;
    if (activeTab === 'completed' && visitor.status !== 'completed') return false;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !String(visitor.id).toLowerCase().includes(term) &&
        !visitor.visitor_name.toLowerCase().includes(term) &&
        // !visitor.villa_number?.toLowerCase().includes(term) &&
        !getVisitorUnitsLabel(visitor).toLowerCase().includes(term) &&
        !(visitor.purpose && visitor.purpose.toLowerCase().includes(term))
      ) {
        return false;
      }
    }

    // Filter by date
    if (filterDate) {
      const visitDate = new Date(visitor.time_in).toLocaleDateString();
      const filterDateStr = new Date(filterDate).toLocaleDateString();
      if (visitDate !== filterDateStr) return false;
    }

    // Filter by villa - check unit_id
    if (filterVilla) {
      const filterVillaId = parseInt(filterVilla);
      if (visitor.unit_id !== filterVillaId) {
        return false;
      }
    }

    return true;
  });

  const totalPages = Math.ceil(filteredVisitors.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredVisitors.slice(indexOfFirstItem, indexOfLastItem);
  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('visitors-table-section');
    }, 50);
  };

  // Previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('visitors-table-section');
      }, 50);
    }
  };

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('visitors-table-section');
      }, 50);
    }
  };

  function formatDateTime(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  // Calculate duration between check in and check out
  const calculateDuration = (timeIn, timeOut) => {
    if (!timeOut) return 'In Progress';

    const start = new Date(timeIn);
    const end = new Date(timeOut);
    const diff = Math.abs(end - start);

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  };

  // Create local placeholder image using data URL
  const createLocalPlaceholder = (text = 'No Image', width = 200, height = 200) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    // Border
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    // Text
    ctx.fillStyle = '#6c757d';
    ctx.font = '16px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, width / 2, height / 2);

    return canvas.toDataURL();
  };

  // Add more robust image URL handling similar to the guard component
  const getSafeImageUrl = (url, fallbackText = 'No Image') => {
    if (!url) return createLocalPlaceholder(fallbackText);

    try {
      // If it's a data URL, return as is
      if (url && url.startsWith('data:image')) {
        return url;
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      // Handle relative URLs for visitor images specifically
      if (url && typeof url === 'string') {
        // If it's already a full daily-visitors URL, just return it (optionally with cache buster)
        if (url.includes('/api/daily-visitors/')) {
          const separator = url.includes('?') ? '&' : '?';
          return `${url}${separator}t=${Date.now()}`;
        }

        // Extract the path portion if it's a full URL
        let imagePath = url;
        if (url.startsWith('http')) {
          try {
            const urlObj = new URL(url);
            imagePath = urlObj.pathname;
          } catch (e) {
            console.log('Error parsing URL:', e);
          }
        }

        // Direct access to visitor photos with bypass parameter
        if (imagePath.includes('/photo/') || imagePath.includes('/visitor_')) {
          return `${apiBaseUrl}/api/visitors/photo/${imagePath.split('/').pop()}?bypass=true&t=${Date.now()}`;
        }

        // Direct access to ID documents with bypass parameter
        if (imagePath.includes('/id-document/') || imagePath.includes('/id_doc_')) {
          return `${apiBaseUrl}/api/visitors/id-document/${imagePath.split('/').pop()}?bypass=true&t=${Date.now()}`;
        }
      }

      // Use the standard getImageUrl function with added parameters
      let processedUrl = getImageUrl(url);
      if (!processedUrl) return createLocalPlaceholder(fallbackText);

      // Ensure bypass parameter is included
      if (!processedUrl.includes('bypass=')) {
        const separator = processedUrl.includes('?') ? '&' : '?';
        processedUrl = `${processedUrl}${separator}bypass=true&t=${Date.now()}`;
      }

      return processedUrl;
    } catch (error) {
      console.log('Error processing image URL:', error);
      return createLocalPlaceholder(fallbackText);
    }
  };

  // More robust image error handling similar to guard component
  const handleImageError = (e, fallbackText = 'No Image') => {
    try {
      const img = e.target;

      // Prevent infinite retry loops
      if (img.hasAttribute('data-retry-attempted')) {
        // If we already tried once, go straight to local placeholder
        img.src = createLocalPlaceholder(fallbackText);
        return;
      }

      img.setAttribute('data-retry-attempted', 'true');

      // Try to get visitor ID from data attributes
      const visitorId = img.getAttribute('data-visitor-id');

      if (visitorId) {
        console.log(`Trying direct visitor image URL for ID: ${visitorId}`);
        // Direct URL approach using specific visitor ID endpoints
        const apiBaseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');
        const isIdDoc = img.alt.toLowerCase().includes('id');
        const endpoint = isIdDoc ? 'id-document' : 'photo';
        const newSrc = `${apiBaseUrl}/api/visitors/${endpoint}/${visitorId}?bypass=true&t=${Date.now()}`;

        // Set new direct URL
        img.src = newSrc;
        return;
      }

      // If no visitor ID, try with API URL and bypass parameter
      const apiBaseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');
      let simplifiedUrl = img.src;

      // If the URL is absolute, extract just the path
      if (simplifiedUrl.startsWith('http')) {
        try {
          const urlObj = new URL(simplifiedUrl);
          simplifiedUrl = urlObj.pathname + urlObj.search;
        } catch (e) {
          console.log('Error parsing URL:', e);
        }
      }

      // Ensure URL starts with /api/
      if (!simplifiedUrl.startsWith('/api/')) {
        simplifiedUrl = simplifiedUrl.startsWith('/')
          ? `/api${simplifiedUrl}`
          : `/api/${simplifiedUrl}`;
      }

      // Add cache busting and bypass parameters
      const separator = simplifiedUrl.includes('?') ? '&' : '?';
      simplifiedUrl = `${simplifiedUrl}${separator}t=${Date.now()}&bypass=true`;

      // Create complete URL
      const newSrc = `${apiBaseUrl}${simplifiedUrl.startsWith('/') ? '' : '/'}${simplifiedUrl}`;

      // Only update if different to prevent loops
      if (newSrc !== img.src) {
        img.src = newSrc;
      } else {
        // Use local placeholder if we can't construct a better URL
        img.src = createLocalPlaceholder(fallbackText);
      }
    } catch (error) {
      console.log('Error in handleImageError:', error);
      e.target.src = createLocalPlaceholder(fallbackText);
    }
  };

  // Update the openVisitorDetails function to better handle image URLs
  const openVisitorDetails = async (visitor) => {
    try {
      console.log('🔍 View button clicked - Opening visitor details for visitor:', visitor);
      console.log('🔍 Current modal state - showDetailsModal:', showDetailsModal);

      // First store the basic visitor info to show immediately
      setSelectedVisitor(visitor);
      setShowDetailsModal(true);

      console.log('🔍 Modal state updated - showDetailsModal should now be true');

      // Then fetch the complete visitor data from API to get photos
      try {
        console.log(`Fetching detailed visitor data for ID: ${visitor.id}`);
        const response = await api.get(`/api/visitors/${visitor.id}`, {
          timeout: 5000,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        console.log('Visitor details API response:', response);

        // Check if we got HTML instead of JSON
        if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
          console.warn('Visitor details API returned HTML instead of JSON');
          // Continue with basic visitor data - don't close modal
          return;
        }

        if (response.data) {
          // Fix photo URL with our improved function
          let photoUrl = null;
          if (response.data.photo_url) {
            photoUrl = getSafeImageUrl(response.data.photo_url);
          }

          // Fix ID document URL with our improved function
          let idDocumentUrl = null;
          if (response.data.id_document_url) {
            idDocumentUrl = getSafeImageUrl(response.data.id_document_url);
          }

          // Update the selected visitor with the detailed data
          setSelectedVisitor({
            ...visitor,
            ...response.data,
            photo_url: photoUrl,
            id_document_url: idDocumentUrl
          });

          console.log('Updated visitor details with photos');
        }
      } catch (detailsError) {
        console.warn("Error fetching visitor details:", detailsError);
        // Don't close the modal, just continue with basic data
        // The modal is already open with basic visitor information

        // Try to construct image URLs from visitor ID if available
        if (visitor.id) {
          const baseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');
          // const photoUrl = `${baseUrl}/api/visitors/photo/${visitor.id}?t=${Date.now()}`;
          // const idDocUrl = `${baseUrl}/api/visitors/id-document/${visitor.id}?t=${Date.now()}`;

          setSelectedVisitor({
            ...visitor,
            photo_url: visitor.photo_url ? getSafeImageUrl(visitor.photo_url) : null,
            id_document_url: visitor.id_document_url ? getSafeImageUrl(visitor.id_document_url) : null
          });
        }
      }
    } catch (err) {
      console.error("Error in openVisitorDetails:", err);
      // Still show the modal with basic data
      setSelectedVisitor(visitor);
      setShowDetailsModal(true);
    }
  };

  // Helper: format single or multiple unit_ids into human-readable villa labels
  const getVisitorUnitsLabel = React.useCallback((visitor) => {
    try {
      // If we have multiple unit_ids and villas loaded, map IDs to villa numbers
      if (visitor.unit_ids && Array.isArray(localVillas) && localVillas.length > 0) {
        const ids = String(visitor.unit_ids)
          .split(',')
          .map(id => id.trim())
          .filter(id => id);

        const labels = ids.map(id => {
          const numId = parseInt(id, 10);
          const villa = localVillas.find(v => v.id === numId);
          const label = villa?.villa_number || `Unit ${id}`;
          return villa?.unit_type ? `${villa.unit_type} ${label}` : label;
        });

        if (labels.length > 0) {
          return labels.join(', ');
        }
      }

      // Fallback to existing single villa_number/unit_type if available
      if (visitor.villa_number) {
        return visitor.unit_type ? `${visitor.unit_type} ${visitor.villa_number}` : visitor.villa_number;
      }

      return 'Unknown';
    } catch (e) {
      console.error('Error getting visitor units label (admin):', e);
      return visitor.villa_number || 'Unknown';
    }
  }, [localVillas]);

  // Handle closing visitor details modal
  const closeVisitorDetails = () => {
    console.log('🔍 Closing visitor details modal');
    setShowDetailsModal(false);
    setSelectedVisitor(null);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval':
        return <span className="badge bg-warning">Pending Approval</span>;
      case 'active':
        return <span className="badge bg-success">Active</span>;
      case 'rejected':
        return <span className="badge bg-danger">Rejected</span>;
      case 'completed':
        return <span className="badge bg-secondary">Completed</span>;
      default:
        return <span className="badge bg-info">{status}</span>;
    }
  };

  // Simple test function to verify modal works
  // const testModal = () => {
  //   console.log('🔍 Testing modal with dummy data');
  //   setSelectedVisitor({
  //     id: 999,
  //     visitor_name: 'Test Visitor',
  //     phone_number: '1234567890',
  //     purpose: 'Testing',
  //     villa_number: 'Test Unit',
  //     time_in: new Date().toISOString(),
  //     time_out: null,
  //     status: 'active'
  //   });
  //   setShowDetailsModal(true);
  // };

  // Handle checking out a visitor
  // const handleCheckOut = async (visitorId) => {
  //   try {
  //     console.log(`Checking out visitor ${visitorId}`);

  //     // Call API to check out the visitor
  //     const response = await api.put(`/api/visitors/${visitorId}/checkout`, {
  //       time_out: new Date().toISOString(),
  //       status: 'completed'
  //     });

  //     console.log('Checkout response:', response.data);

  //     // Update local state
  //     const updatedVisitors = localVisitors.map(visitor => 
  //       visitor.id === visitorId 
  //         ? { 
  //             ...visitor, 
  //             time_out: new Date().toISOString(),
  //             status: 'completed'
  //           }
  //         : visitor
  //     );

  //     setLocalVisitors(updatedVisitors);

  //     // Update stats
  //     updateVisitorStats(updatedVisitors, localVillas);

  //     console.log('Visitor checked out successfully');
  //   } catch (error) {
  //     console.error('Error checking out visitor:', error);
  //     alert('Failed to check out visitor. Please try again.');
  //   }
  // };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, filterDate, filterVilla]);

  if ((loading || dataLoading) && localVisitors.length === 0) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="page-title">
            <i className="fas fa-users"></i> Visitor Management
          </h1>
          <div>
            <button
              className="btn btn-outline-secondary shadow-sm"
              onClick={() => {
                clearAllTimers();
                setLoading(false);
              }}
            >
              <i className="fas fa-stop-circle me-2"></i>
              Stop Loading
            </button>
          </div>
        </div>

        <div className="d-flex justify-content-center align-items-center visitor-loading-container">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3 visitor-loading-spinner" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading visitor data...</p>
            <button
              className="btn btn-primary mt-3"
              onClick={handleManualRefresh}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!loading && !dataLoading && localVisitors.length === 0) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="page-title">
            <i className="fas fa-users"></i> Visitor Management
          </h1>
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary shadow-sm"
              onClick={handleManualRefresh}
              disabled={loading}
            >
              <i className={`fas ${loading ? 'fa-sync fa-spin' : 'fa-sync-alt'} me-2`}></i>
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger shadow-sm border-0">
            <div className="d-flex align-items-center">
              <i className="fas fa-exclamation-circle fa-2x me-3"></i>
              <div>
                <h6 className="fw-bold mb-1">Error</h6>
                <p className="mb-0">{error}</p>
                {connectionError && (
                  <small className="text-danger">
                    The application is currently offline. Data shown may not be current.
                  </small>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="alert alert-info shadow-sm border-0 my-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-info-circle fa-2x me-3"></i>
            <div>
              <h6 className="fw-bold mb-1">No Visitor Data Available</h6>
              <p className="mb-0">There are currently no visitors recorded in the system. New visitors will appear here once they are added.</p>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm rounded-lg mt-4">
          <div className="card-body p-5 text-center">
            <div className="mb-4">
              <i className="fas fa-users text-muted visitor-empty-icon"></i>
            </div>
            <h4 className="mb-3">No Visitor Records Found</h4>
            <p className="text-muted mb-4">Visitor data will appear here once guard staff check in visitors or owners pre-register their guests.</p>
            <button
              className="btn btn-primary"
              onClick={handleManualRefresh}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 activity-container">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <h1 className="page-title">
          <i className="fas fa-users"></i> Visitor Management
        </h1>
        <div>
          {connectionError && (
            <span className="badge bg-danger me-3 py-2 px-3">
              <i className="fas fa-wifi-slash me-1"></i> Offline
            </span>
          )}
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary shadow-sm"
              onClick={handleManualRefresh}
              disabled={loading}
            >
              <i className={`fas ${loading ? 'fa-sync fa-spin' : 'fa-sync-alt'} me-2`}></i>
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger shadow-sm border-0">
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-circle fa-2x me-3"></i>
            <div>
              <h6 className="fw-bold mb-1">Error</h6>
              <p className="mb-0">{error}</p>
              {connectionError && (
                <small className="text-danger">
                  The application is currently offline. Data shown may not be current.
                </small>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="visitor-stats-container">
        <div className="visitor-stat-card today">
          <div className="stat-title">TODAY&apos;S VISITORS</div>
          <div className="stat-value" title={visitorStats.todayCount}>
            {visitorStats.todayCount}
          </div>
          <div className="stat-description">
            Visitors checked in today
          </div>
        </div>

        <div className="visitor-stat-card week">
          <div className="stat-title">THIS WEEK</div>
          <div className="stat-value" title={visitorStats.weekCount}>
            {visitorStats.weekCount}
          </div>
          <div className="stat-description">
            Visitors in the last 7 days
          </div>
        </div>

        <div className="visitor-stat-card month">
          <div className="stat-title">THIS MONTH</div>
          <div className="stat-value" title={visitorStats.monthCount}>
            {visitorStats.monthCount}
          </div>
          <div className="stat-description">
            Visitors in the last 30 days
          </div>
        </div>

        <div className="visitor-stat-card active">
          <div className="stat-title">ACTIVE VISITORS</div>
          <div className="stat-value" title={localVisitors.filter(v => v.status === 'active').length}>
            {localVisitors.filter(v => v.status === 'active').length}
          </div>
          <div className="stat-description">
            Currently on premises
          </div>
        </div>
      </div>

      {/* Top Villas by Visitors */}
      <div className="card border-0 shadow-sm rounded-lg mb-4">
        <div className="card-header visitor-card-header">
          <h5 className="mb-0 fw-bold visitor-card-title">
            <i className="fas fa-chart-bar me-2 visitor-card-icon"></i>
            {unitPlural} with Most Visitors
          </h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="border-0">{unitSingular} Number</th>
                  <th className="border-0">Type</th>
                  <th className="border-0">Total Visitors</th>
                  <th className="border-0">Currently Active</th>
                </tr>
              </thead>
              <tbody>
                {visitorStats.byVilla.length > 0 ? (
                  visitorStats.byVilla.map(villa => (
                    <tr key={villa.villa_id}>
                      <td className="fw-bold">{villa.villa_number}</td>
                      <td><span className="text-muted">{villa.unit_type || 'Unit'}</span></td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="badge rounded-pill bg-primary me-2">{villa.count}</span>
                          <div className="progress flex-grow-1 visitor-progress-bar">
                            <div
                              className="progress-bar"
                              style={{
                                width: `${Math.min(100, (villa.count / Math.max(...visitorStats.byVilla.map(v => v.count))) * 100)}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {villa.active > 0 ? (
                          <span className="badge rounded-pill bg-success">{villa.active} active</span>
                        ) : (
                          <span className="badge rounded-pill bg-light text-muted">None</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-3 text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      No visitor data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Unified Search & Filter + Tabs Section */}
      <div className="visitor-search-filter-section">
        <div className="visitor-search-filter-title">
          <i className="fas fa-filter me-2"></i> Search & Filter
        </div>
        <div className="d-flex flex-wrap g-3">
          <div className="col-md-3">
            <div className="input-group input-group-lg">
              <span className="input-group-text bg-white border-end-0">
                <i className="fas fa-search text-primary"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder="Search visitor name, unit..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-3">
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0">
                <i className="fas fa-calendar text-primary"></i>
              </span>
              <input
                type="date"
                className="form-control border-start-0 ps-0"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-3">
            <select
              className="form-select"
              value={filterVilla}
              onChange={e => setFilterVilla(e.target.value)}
            >
              <option value="">All {unitPlural}</option>
              {localVillas.map(villa => (
                <option key={villa.id} value={villa.id}>
                  {villa.unit_type || unitSingular} {villa.villa_number}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <div className="d-flex justify-content-between">
              <button
                className="btn btn-light px-4"
                onClick={() => {
                  setSearchTerm('');
                  setFilterDate('');
                  setFilterVilla('');
                }}
              >
                <i className="fas fa-times me-2"></i>
                Clear Filters
              </button>
            </div>
          </div>

        </div>
        {/* Visitor Tabs */}
        <div className="visitor-tabs-bar mb-3">
          <button
            className={`visitor-tab-btn${activeTab === 'all' ? ' active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <i className="fas fa-list me-2"></i> All Visitors
          </button>
          <button
            className={`visitor-tab-btn${activeTab === 'active' ? ' active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            <i className="fas fa-walking me-2"></i> Active Visitors
          </button>
          <button
            className={`visitor-tab-btn${activeTab === 'completed' ? ' active' : ''}`}
            onClick={() => setActiveTab('completed')}
          >
            <i className="fas fa-check-circle me-2"></i> Completed Visits
          </button>

          <span className="badge bg-info py-2 px-3 d-flex align-items-center">
            <i className="fas fa-users me-2"></i>
            {filteredVisitors.length} visitors found
          </span>
        </div>
      </div>

      {/* Visitors Table */}
      <div className="card border-0 shadow-sm rounded-lg" id="visitors-table-section">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table  table-striped table-hover align-middle mb-0 visitor-table">
              <thead className="bg-light">
                <tr>
                  <th className="py-3">ID</th>
                  <th className="py-3">Visitor</th>
                  <th className="py-3">{unitSingular}</th>
                  <th className="py-3">Purpose</th>
                  <th className="py-3">Check In</th>
                  <th className="py-3">Check Out</th>
                  <th className="py-3">Duration</th>
                  <th className="py-3">Status</th>
                  <th className="py-3 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map(visitor => (
                    <tr key={visitor.id}>
                      <td className="fw-bold text-primary">{visitor.id}</td>
                      <td className="fw-medium text-nowrap-mobile">{visitor.visitor_name}</td>
                      <td>
                        <span className="badge rounded-pill bg-light text-dark border">
                          {getVisitorUnitsLabel(visitor)}
                        </span>
                      </td>
                      <td><span className="text-muted text-nowrap-mobile">{visitor.purpose || 'General Visit'}</span></td>
                      <td className="text-nowrap">
                        <small>
                          {visitor.time_in ? (
                            <>
                              <i className="fas fa-sign-in-alt me-1 text-success"></i>
                              {formatDateTime(visitor.time_in)}
                            </>
                          ) : (
                            <span className="text-muted fst-italic">
                              <i className="fas fa-hourglass-half me-1"></i>
                              Not yet checked in
                            </span>
                          )}
                        </small>
                      </td>
                      <td className="text-nowrap">
                        <small>
                          {visitor.time_out ? (
                            <>
                              <i className="fas fa-sign-out-alt me-1 text-danger"></i>
                              {formatDateTime(visitor.time_out)}
                            </>
                          ) : (
                            <span className="text-muted fst-italic">
                              <i className="fas fa-hourglass-half me-1"></i>
                              Not yet checked out
                            </span>
                          )}
                        </small>
                      </td>
                      <td>
                        {visitor.status === 'active' ? (
                          <span className="badge rounded-pill bg-warning text-dark">
                            <i className="fas fa-clock me-1"></i>
                            {calculateDuration(visitor.time_in, null)}
                          </span>
                        ) : (
                          <span className="badge rounded-pill bg-light text-muted">
                            <i className="fas fa-check me-1"></i>
                            {calculateDuration(visitor.time_in, visitor.time_out)}
                          </span>
                        )}
                      </td>
                      <td>
                        {getStatusBadge(visitor.status)}
                      </td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => openVisitorDetails(visitor)}
                            title="View Details"
                            style={{
                              whiteSpace: 'nowrap',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            <i className="fas fa-eye" style={{ flexShrink: 0 }}></i>
                            <span className="d-none d-md-inline ms-1">View</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-muted">
                      <i className="fas fa-users fa-3x mb-3 d-block"></i>
                      <p>No visitors found matching your criteria.</p>
                      <p>Try adjusting your search or filters.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Visitor Details Modal */}
      {showDetailsModal && selectedVisitor && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                <h5 className="modal-title">
                  <i className="fas fa-user-tag me-2"></i>
                  Visitor Details #<span className="fw-bold">{selectedVisitor.id}</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeVisitorDetails}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body p-3">
                {/* Visitor Name & Phone */}
                <div className="text-center mb-3">
                  <h5 className="fw-bold">{selectedVisitor.visitor_name}</h5>
                  <p className="text-muted">
                    <i className="fas fa-phone me-1"></i>
                    {selectedVisitor.phone_number || selectedVisitor.phone || 'No phone number'}
                  </p>
                </div>

                {/* Photos - Side by Side */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  {/* Visitor Photo */}
                  <div className="col-lg-6 text-center">
                    <small className="text-muted d-block mb-1">Visitor Photo</small>
                    <img
                      src={
                        selectedVisitor.photo
                          ? (selectedVisitor.photo.startsWith('data:') ? selectedVisitor.photo : `data:image/jpeg;base64,${selectedVisitor.photo}`)
                          : (selectedVisitor.photo_url
                            ? getSafeImageUrl(selectedVisitor.photo_url, 'No Photo')
                            : createLocalPlaceholder('No Photo'))
                      }
                      alt="Visitor"
                      className="img-fluid img-thumbnail visitor-detail-image"
                      data-visitor-id={selectedVisitor.id}
                      onError={(e) => handleImageError(e, 'No Photo')}
                      crossOrigin="anonymous"
                    />
                  </div>

                  {/* ID Document */}
                  <div className="col-6 text-center">
                    <small className="text-muted d-block mb-1">ID Document</small>
                    <img
                      src={
                        selectedVisitor.id_document
                          ? (selectedVisitor.id_document.startsWith('data:') ? selectedVisitor.id_document : `data:image/jpeg;base64,${selectedVisitor.id_document}`)
                          : (selectedVisitor.id_document_url
                            ? getSafeImageUrl(selectedVisitor.id_document_url, 'No ID Document')
                            : createLocalPlaceholder('No ID Document'))
                      }
                      alt="ID Document"
                      className="img-fluid img-thumbnail visitor-detail-image"
                      data-visitor-id={selectedVisitor.id}
                      onError={(e) => handleImageError(e, 'No ID Document')}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>

                {/* Visit Details */}
                <div className="border-top pt-3 mb-3">
                  <div className="mb-2">
                    <div className="d-flex justify-content-between">
                      <strong>Purpose of Visit:</strong>
                      <span>{selectedVisitor.purpose || 'General Visit'}</span>
                    </div>
                  </div>

                  <div className="mb-2">
                    <div className="d-flex justify-content-between">
                      <strong>Visiting Unit:</strong>
                      <span>{getVisitorUnitsLabel(selectedVisitor)}</span>
                    </div>
                  </div>

                  <div className="mb-2">
                    <div className="d-flex justify-content-between">
                      <strong>Check In Time:</strong>
                      {selectedVisitor.time_in ? (
                        <>
                          <span className="text-success">{formatDateTime(selectedVisitor.time_in)}</span>
                        </>
                      ) : (
                        <span className="text-muted fst-italic">
                          <i className="fas fa-hourglass-half me-1"></i>
                          Not yet checked in
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mb-2">
                    <div className="d-flex justify-content-between">
                      <strong>Check Out Time:</strong>
                      <span className="text-danger">
                        {selectedVisitor.time_out ? (
                          <>
                            <span className="text-danger">{formatDateTime(selectedVisitor.time_out)}</span>
                          </>
                        ) : (
                          <span className="text-muted fst-italic">
                            <i className="fas fa-hourglass-half me-1"></i>
                            Not yet checked out
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="mb-2">
                    <div className="d-flex justify-content-between">
                      <strong>Duration:</strong>
                      <span>{calculateDuration(selectedVisitor.time_in, selectedVisitor.time_out)}</span>
                    </div>
                  </div>

                  <div className="mb-2">
                    <div className="d-flex justify-content-between">
                      <strong>Current Status:</strong>
                      {getStatusBadge(selectedVisitor.status)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={closeVisitorDetails}>
                  <i className="fas fa-times me-1"></i>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    closeVisitorDetails();
                  }}
                >
                  <i className="fas fa-check me-1"></i>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {currentItems.length > 0 && (
        <div className="card-footer d-flex justify-content-between align-items-center">
          <div>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredVisitors.length)} of {filteredVisitors.length} visitors
          </div>

          <nav aria-label="Visitor pagination">
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                  <i className="fas fa-chevron-left"></i>
                </button>
              </li>

              {/* Simplified pagination - show only certain page numbers */}
              {Array.from({ length: totalPages }).map((_, index) => {
                const pageNumber = index + 1;

                // Show first page, last page, current page, and pages around current page
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => paginate(pageNumber)}>
                        {pageNumber}
                      </button>
                    </li>
                  );
                }

                // Show ellipsis
                if (
                  pageNumber === 2 && currentPage > 3 ||
                  pageNumber === totalPages - 1 && currentPage < totalPages - 2
                ) {
                  return (
                    <li key={pageNumber} className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }

                return null;
              })}

              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default Visitors; 